import axios from '@/plugins/axios'
import { AxiosResponse } from 'axios'
import jwtDecode from 'jwt-decode'
import { useStore, Module } from 'vuex'
import { setCookie, getCookie } from '@/utils/cookie'
import { AbilityBuilder, Ability } from '@casl/ability'
import * as Sentry from '@sentry/vue'

import { 
	canReadWorkspaceMembers,
	canReadWorkspaceSchedules,
	canReadStackRankReports,
	canReadIndustrySnapshotReports,
	canReadSmartSearchReports,
	canReadPeerPerformanceReports
 } from '@/utils/rbac'

interface AuthState {
    roles: string[]
    disabledRoles: string[]
    abilities: string[]
    token: string | null
    error_code: string | null
    error_message: string | null
    authenticated: boolean
    authenticatedLimited: boolean
    passwordEnabled: boolean
    ssoEnabled: boolean
    refresh_token_valid: boolean
    abortController: null,
    username: null,
}

export interface Credentials {
    username: string
    password: string
    workspaceUuid: string
	recaptcha_token?: string
}

var refreshPromise = null

const refreshTokenExists = () => {
  // Because we can't directly see the refresh_token cookie -- it's http-only for security -- 
  // we track its existence with another non-sensitive non-http-only cookie that has the same settings.
  return document.cookie.indexOf('rfttrack') > -1
}

const authModule: Module<AuthState, any> = {
    state: {
        authenticated: false,
        authenticatedLimited: false,
        signupEnabled: false,
        passwordEnabled: false,
        ssoEnabled: false,
        roles: [],
        disabledRoles: [],
        abilities: [],
        eventSourceUrl: null,
        selectedVersion: null,
        token: null,
        error_code: null,
        error_message: null,
        refresh_token_valid: refreshTokenExists(),
        // We'll mark it invalid on the first 401 error.
        workspace: {},
        originalState: {},
        workspaceAuthSettings: { samlRequired: null },
        authUsernameSamlRequirements: {},
        authedMemberInfo: {},
    },
    actions: {
    	  async getSuperadminWorkspaces({ commit, state, dispatch }, params) {
						const { data } = await axios.get(
								'/api/workspaces/all',
								{params: {
									order: params.order ?? '',
									itemPerPage: params.itemPerPage ?? 100,
									page: params.page ?? 1,
									active: params.active ?? null,
									'20only': params['20only'] ?? null,
								}}
						)
						return data
			  },
    	  async getSuperadminWorkspaceMembers({ commit, state, dispatch }, params) {
						const { data } = await axios.get(
								'/api/workspace_members/all',
								{params: {
									seatType: params.seatType ?? null,
									workspaceUuid: params.workspaceUuid,
									itemPerPage: params.itemPerPage ?? 100,
								}}
						)
						return data
			  },
    	  async getImpersonateableWorkspaces({ commit, state }, username) {
						const { data } = await axios.get(
								'/api/workspaces',
								{'X-Switch-User' : username},
						)
						return data
			  },
        
        async getSamlRequirements({ commit, state}, username) {
        
        		if ((state.authUsernameSamlRequirements?.samlRequired ?? null) !== null) {
        			  return state.authUsernameSamlRequirements
        		}
        
            const { data } = await axios.post(
                `/api/workspaces-public/saml-settings`,
                {username: username}
            )
            
            commit('authUsernameSamlRequirements', data)

            return data
        },

        async clearSamlRequirements({ commit, state}, username) {             
            return commit('authUsernameSamlRequirements', {})
        },

    	  async impersonate({ commit, state, dispatch, getters }, {username, workspaceUuid, token}) {

						if (getters.isImpersonating) {
							throw { response: {statusText: 'Already impersonating.'} }
						}

						if (!getters.isAuthenticated) {
							throw { response: {statusText: 'Must be authenticated to impersonate.'} }
						}
						
						const abortController = new AbortController()
						
						const requestOpts = {
							headers: {
								'X-Switch-User' : username,  
								'X-Switch-User-Workspace': workspaceUuid, 
							},
							signal: abortController.signal
						}
						
						let auth = {}
						let authedMemberInfo = {}
						let workspace = {}
						let workspaceSettings = {}

						const workspaceMemberRequest = axios.get(
								'/api/workspace_members/current',
								requestOpts,
						).then(({data})=> {
							
								auth = {
                	roles: data.roles, 
                	abilities: data.abilities,
                	authedMemberInfo: {
										 id: data.id,
										 first_name: data.firstName,
										 last_name: data.lastName,
										 timezone: data.timezone,
										 timezone_autodetect: data.timezoneAutoUpdate,
										 extensionInstalled: data.extensionInstalled,
									},
                }
                						
						})
						
						const workspaceRequest = axios.get(
								'/api/workspaces/current',
								requestOpts,
						).then(w => {
                workspace = w.data
						})

					 const workspaceSettingsRequest = axios.get(
								'/api/workspaces/current/settings',
								requestOpts,
						).then(ws => {
              workspaceSettings = ws.data
						})

						return Promise.all([workspaceMemberRequest, workspaceRequest, workspaceSettingsRequest])
						 .then(async () => {
							  const imp = commit('impersonate', {
							  		... auth,
							  		username: username,
							  		workspace: {
											 id: workspace.id,
											 uuid: workspace.uuid,
											 logo: workspace.logo,
											 logo_reverse : workspace.logoReverse,
											 logo_background_color :  workspace.logoBackgroundColor,
											 earned_media_label :  workspaceSettings.mediaPointsLabel,
											 earned_media_prefix :  workspaceSettings.mediaPointsPrefix,
											 earned_media_multi_clicks :  workspaceSettings.mediaPointsMultipliers.clicks,
											 earned_media_multi_likes :  workspaceSettings.mediaPointsMultipliers.reactions,
											 earned_media_multi_comments :  workspaceSettings.mediaPointsMultipliers.comments,
											 earned_media_multi_shares :  workspaceSettings.mediaPointsMultipliers.posts,
											 feature_flags :  workspaceSettings.featureFlags,
										}
							  })
							  
								await dispatch('getRoles', {refresh: true})
								
								return imp
							  
						}).catch(error => {
							if (error.cause?.response?.status == 403) {
								  throw new Error(error.cause.response.data.detail, error.cause)
							}
							abortController.abort()
						})
    	  	
    	  },
    	  async removeImpersonate({ commit, state, dispatch }) {
    	  	  
    	  	  if (!state.originalState) {
    	  	  	return
    	  	  }

						state.abortController?.abort()
						state.abortController = null
						
						await commit('authenticate', state.originalState)
						
    	  		sessionStorage.setItem('impersonating', '')
    	  		sessionStorage.setItem('impersonatingWorkspace', '')

						await dispatch('getRoles', {refresh: true})
    	  		
    	  },
    	  async initRoles({ commit, state, dispatch }) {

						if (state.disabledRoles.length && state.roles.length) {
							 // We've already selected a subset of roles,
							 // probably stored in persisted state.
							 // Still need to dispatch useRoles so reactivity that depends on role changes gets called.
							 return dispatch('useRoles', [state.roles[0]])
						}

						return dispatch('getRoles').then(roles => {
							state.roles.forEach(r => {
								const role = roles.items.find(role => role.slug == r)
								if (role?.isBase) {
									 return dispatch('useRoles', [role.slug])
								}
							})
						})
    	  		
    	  },
        async emailVerify({ commit, state, dispatch }, hashParams) {
            try {
                
                const response = await axios.post(
                    '/email_verify',
                    hashParams,
                    { skipAuthRefresh: true },
                )
                
                if (!response?.data?.data) {
                	  throw { response: {statusText: "Unknown error"} }
                }

                return commit('authenticate', {
                    authenticated: response.data.data.profile.auth,
                    authenticatedLimited: response.data.data.profile.auth_limited,
                    passwordEnabled: response.data.data.profile.password_enabled,
                    ssoEnabled: response.data.data.profile.sso_enabled,
                    token: response.data.token,
                    roles: response.data.data.roles,
                    abilities: response.data.data.abilities,
                    username: response.data.data.username,
                    workspace: response.data.data.workspace,
                    authedMemberInfo: response.data.data.profile,
                })
 
             } catch (error) {
            
                commit('authenticateError', {
                    code: error?.response?.data?.code,
                    message: error?.response?.data?.message,
                })
                
                throw error

            }
        },
        async login({ commit, state, dispatch, getters }, creds: Credentials) {
            try {
                
                const response = await axios.post(
                    '/authentication_token',
                    creds,
                    { skipAuthRefresh: true },
                )
                
                if (!response?.data?.data) {
                	  throw { response: {statusText: "Unknown error"} }
                }

                await commit('authenticate', {
                    authenticated: response.data.data.profile.auth,
                    authenticatedLimited: response.data.data.profile.auth_limited,
                    passwordEnabled: response.data.data.profile.password_enabled,
                    ssoEnabled: response.data.data.profile.sso_enabled,
                    token: response.data.token,
                    roles: creds.workspaceUuid ? response.data.data.roles : [],
                    abilities: response.data.data.abilities,
                    username: response.data.data.username,
                    workspace: response.data.data.workspace,
                    authedMemberInfo: response.data.data.profile,
                })
                
                if (getters.isAuthenticated && getters.roles.length) {
                	  return dispatch('initRoles')
                }
                
                return
  
             } catch (error) {
            
            	  console.log(error)
            
                commit('authenticateError', {
                    code: error?.response?.data?.code || error?.code,
                    message: error?.response?.data?.message || error?.message,
                })

            }
        },
        refreshToken({ commit, state, dispatch, getters }, options = {}) {
            
            if (getters.isRefreshTokenValid === false) {
                return Promise.reject('Invalid refresh token')
            }
                        
            let workspaceUuid = options?.noWorkspace ? null : getters.loginWorkspaceUuid

            if (!workspaceUuid) {
            	  /*
            	  console.log('Invalid workspace')
                commit('authenticate', {})
                return Promise.reject('Invalid workspace')
                */
            }
            
            if (refreshPromise) {
            	 return refreshPromise
            }
            
            state.refreshing = true

            refreshPromise = axios
                .post('/token/refresh', { workspaceUuid: workspaceUuid })
                .then(async (response: AxiosResponse) => {
									
										if (!response?.data?.data) {
												throw { response: {statusText: "Unknown error"} }
										}
										
										const impersonating = sessionStorage.getItem('impersonating')
										const impersonatingWorkspace = sessionStorage.getItem('impersonatingWorkspace')

 										commit('authenticate', {
												authenticated: response.data.data.profile.auth,
												authenticatedLimited: response.data.data.profile.auth_limited,
												passwordEnabled: response.data.data.profile.password_enabled,
												ssoEnabled: response.data.data.profile.sso_enabled,
												token: response.data.token,
												roles: response.data.data.roles,
												abilities: response.data.data.abilities,
												username: response.data.data.username,
												workspace: response.data.data.workspace,
												authedMemberInfo: response.data.data.profile
										})
										
										if (getters.isAuthenticated && getters.roles.length) {
												await dispatch('initRoles')
										}
                    
                    if (impersonating && impersonatingWorkspace) {
                    	 console.log('Restoring impersonation ...')
                    	 console.log(impersonating)
                    	 console.log(impersonatingWorkspace)
                    	 return dispatch('impersonate', {
                    	 		username: impersonating, 
                    	 		workspaceUuid: impersonatingWorkspace,
                    	 	}).then(() => dispatch('getRoles', {refresh: true}))
                    } else {
											return 
                    }
                    
                 }).catch(error => {
                 
										return commit('authenticateError', {
											code: error?.data ? error?.data.code : null,
											message: error?.data ? error?.data.message : null,
										})
										
										// throw error
										
								}).finally(() => {
									state.refreshing = false
									refreshPromise = null
								})
							
						return refreshPromise
        },

				useRoles({ state, getters, dispatch, commit }, roles: string[]) {
							
						const allRoles = new Set(getters.allRoles)
						const newRoles = new Set(roles)
						
						if (allRoles.has('superadmin')) {
							newRoles.add('superadmin')
						}
											  
						return dispatch('getRoles').then(workspaceRoles => {
  					  const abilities = {}
							newRoles.forEach(r => {
								const roleAbilities = workspaceRoles.items.find(wr => wr.slug == r)?.abilities ?? {}
								Object.keys(roleAbilities).forEach(a => {
									abilities[a] = [ ... abilities[a] ?? [], roleAbilities[a]]
								})
							})

							return commit(
								 'activeRoles', 
								 {
										enabledRoles: Array.from(allRoles.intersection(newRoles).values()),
										disabledRoles: Array.from(allRoles.difference(newRoles).values()),
										abilities: abilities,
								 }
							)

						})

				},

        can({ state, getters }, { ability, ownerId }) {
                    
            if (ability.length === 1) {
                if (ability[0] === '*') return true
                return typeof state.abilities === 'object' && Object.keys(state.abilities).length > 0
            }
        
            if (ability.length < 2) {
               throw new Error('Invalid ability')
            }

            /*
             * This weird bug took a few hours to find. LOL
             * JS gives reference instead of value when using non-scalar types.
             * Using `shift()` on parameters directly modifies the original array.
             * So we always need to clone the array if it's going to be modified.
             * Using spread operator (...) is the easiest way.
             * Commented by @HarunRRayhan to save time for me and others.
             */
            const newAbilityArray = [...ability] // cloning the array to save from that weired bug
            const action = newAbilityArray.shift()
            const subject = newAbilityArray.join(' ')
            
            let authedWorkspaceMemberId = getters.authedMemberInfo?.id

            return getters.ability.can(action, subject) && (subject.indexOf('my-') !== 0 || authedWorkspaceMemberId == ownerId)

        },
        logout({ commit, state }) {

        		state.abortController?.abort() // Abort pending requests
        		state.abortController = null
			localStorage.removeItem('last_activity_ts')
        		
            const invalidateRequest = axios.get('/api/token/invalidate', {silent: true}).catch(e => e)
            commit('deauthenticate', {})

            commit('clearWorkspaceFilters')
            commit('clearCampaignFilters')
            commit('clearActivityFilters')
			commit('clearCustomization')
            return invalidateRequest
        },
        reauthenticate({ commit }) {
            commit('authenticate', {})
            commit('setUsername', null)
            commit('setProfile', {})
        },
    },
    getters: {
        getHome: (state) => {
            if (state.home === '/') {
                console.log('state.home points to the login page, resetting')
                state.home = null
            }
            return state.home
        },
        allRoles: (state) => {
        	return Array.from(new Set([ ... state.roles, ... state.disabledRoles]))
        },
        abortController: state => state.abortController,
        getAuth: (state) => state,
        authedName: (state) => ((state.authedMemberInfo.first_name ?? '') + ' ' + (state.authedMemberInfo.last_name ?? '')).trim(),
        isRefreshing: state => state.refreshing,
        isAuthenticated: (state) => state.authenticated,
        isAuthenticatedLimited: (state) => state.authenticatedLimited,
        isPasswordAuthEnabled: (state) => state.passwordEnabled,
        isPasswordAuthAllowed: (state) => state.workspaceAuthSettings?.samlRequired !== null ? !state.workspaceAuthSettings?.samlRequired : state.authUsernameSamlRequirements.passwordAuthAllowed,
        isRefreshTokenValid: state => state.refresh_token_valid,
        isTokenExpired: (state) => {
            const token = state.token
            const decoded = jwtDecode(token)
            const expired = Date.now() >= decoded.exp * 1000
            return expired
        },
        roles: state => state.roles,
        eventSourceUrl: state =>  state.eventSourceUrl,
        selectedVersion: state => state.selectedVersion ?? null,
        abilities: state => state.abilities,
        ability: state => {
        
        	const { can, rules } = new AbilityBuilder(Ability);

					for (const subject in state.abilities) {
							for (const action of state.abilities[subject]) {
                                can(action, subject)
							}
					}

					return new Ability(rules)

        },

        pointsMultipliers: (state) => {
            return {
                prefix: state.workspace.earned_media_prefix,
                clicks: state.workspace.earned_media_multi_clicks,
                likes: state.workspace.earned_media_multi_likes,
                comments: state.workspace.earned_media_multi_comments,
                shares: state.workspace.earned_media_multi_shares,
            }
        },
        loginWorkspaceUuid: (state) => state.originalState?.workspace?.uuid ?? state.workspaceAuthSettings.workspaceUuid ?? null,
        loginWorkspaceName: (state) => state.originalState?.workspace?.workspaceName ?? state.workspaceAuthSettings.workspaceName ?? null,
        isImpersonating: (state) => !! state.originalState,
        isSamlRequired: (state) => state.workspaceAuthSettings?.samlRequired !== null ? state.workspaceAuthSettings?.samlRequired : (state.authUsernameSamlRequirements?.samlRequired ?? null),
        samlSlug: (state) => state.workspaceAuthSettings.samlSlug ?? state.authUsernameSamlRequirements.samlSlug,
        workspaceUuid: (state) => state.workspace.uuid ?? state.workspaceAuthSettings.workspaceUuid,
        workspace: (state) => state.workspace,
        token: (state) => state.token,
        authedMemberInfo: (state) => state.authedMemberInfo,
        authUsername: (state) => state.authUsername,
        subdomain: (state) => state.workspaceAuthSettings.subdomain,
        allowVersionSwitch: (state) => state.workspaceAuthSettings.allowVersionSwitch ?? null,
        workspaceDefaultVersion: (state) => {
        	return parseFloat(state.workspaceAuthSettings.workspaceDefaultVersion) < 2 ? 1 : 2
        },
        isSsoEnabled: (state) => state.ssoEnabled,
        username: (state) => state.username,
        getWorkspaceFeatureFlags: (state) =>
            state.workspace?.feature_flags ?? [],
        isContentEnabledInWorkspace: (state) =>
            !!state.workspace?.feature_flags?.find((f) => f === 'content'),

    },

    mutations: {
        setAbortController(state: any, controller) {
        	  state.abortController = controller
        },
        selectVersion(state: any, version) {
        	  if (state.workspaceAuthSettings.allowVersionSwitch !== true) {
        	  	  console.log('Version switch not allowed, will not set selected version')
        	  	  return 
        	  }
        		console.log('Version set: ' + version)
        	  state.selectedVersion = parseInt(version)
              setCookie('v', version)
        },
        authenticate(state: any, auth: any) {

						Sentry.setUser({ 
							id: auth.authedMemberInfo.id, 
							username: auth.username
						});
						
						state.disabledRoles = auth.roles.filter(r => state.disabledRoles.indexOf(r) > -1) // disabledRoles persist in localstorage, so make sure we don't 
																																															// carry over any disabled roles that aren't present in this session
						state.authenticated = auth.authenticated
						state.authenticatedLimited = auth.authenticatedLimited
						state.token = auth.token
						state.roles = auth.roles.filter(r => state.disabledRoles.indexOf(r) === -1)
						state.abilities = auth.abilities
						state.error_code = null
						state.error_message = null
						state.username = auth.username
						state.workspace = auth.workspace
						state.authedMemberInfo = auth.authedMemberInfo
						state.passwordEnabled = auth.passwordEnabled
						state.ssoEnabled = auth.ssoEnabled
						state.refresh_token_valid = true
						state.eventSourceUrl = null

						if (state.workspace?.uuid && Object.keys(state.abilities ?? {}).length == 0) {
							state.home = '/signup/resume'
						} else if (canReadWorkspaceSchedules()) {
							state.home = '/dashboard'
						} else if (canReadStackRankReports()) {
							state.home = '/reporting/stack-rank'
						} else if (canReadIndustrySnapshotReports()) {
							state.home = '/reporting/industry-snapshot'
						} else if (canReadSmartSearchReports()) {
							state.home = '/reporting/smartsearch'
						} else if (canReadPeerPerformanceReports()) {
							state.home = '/reporting/peer-performance'
						} else if (canReadWorkspaceMembers()) {
							state.home = '/workspace/'
						} else if (auth.authedMemberInfo?.id) {
							state.home = '/member/' + auth.authedMemberInfo.id
						} else {
							console.log('Could not determine home URL!')
							state.home = '/dashboard' // Have something to fall back on
						}

						state.originalState = null

        },
        impersonate(state: any, auth: any) {

						state.originalState = { ... state }

						state.roles = auth.roles
						state.abilities = auth.abilities
						state.error_code = null
						state.error_message = null
						state.username = auth.username
						state.workspace = auth.workspace
						state.authedMemberInfo = auth.authedMemberInfo
						
						state.eventSourceUrl = null

    	  		sessionStorage.setItem('impersonating', auth.username)
    	  		sessionStorage.setItem('impersonatingWorkspace', auth.workspace.uuid)

        },
        activeRoles(state, {enabledRoles, disabledRoles, abilities}) {
        	  state.abilities = abilities
        	  state.roles = enabledRoles
        	  state.disabledRoles = disabledRoles
        },
        eventSourceUrl(state: any, url) {
        	  state.eventSourceUrl = url
        },
        authenticateError(state: any, {code, message}) {
						state.error_code = code
						state.error_message = message
						state.authenticated = false
						state.token = null
						state.roles = []
						state.abilities = []
						state.username = null
						state.workspace = {}
						state.refresh_token_valid = false
						state.eventSourceUrl = null
        },
        
        deauthenticate(state, _) {
						state.roles = []
						state.abilities = []
						state.authenticated = false
						state.authenticatedLimited = false
						state.token = null
						state.error_code = null
						state.error_message = null
						state.username = null
						state.authedMemberInfo = {}
						state.refresh_token_valid = false
						state.originalState = null
						state.workspace = {}
						state.eventSourceUrl = null
						localStorage.removeItem('last_activity_ts')
    	  		sessionStorage.setItem('impersonating', '')
    	  		sessionStorage.setItem('impersonatingWorkspace', '')
        },
                
        workspaceAuthSettings(state: any, settings) {
            state.workspaceAuthSettings = { ... settings }
        },

        authUsernameSamlRequirements(state: any, data) {
            state.authUsernameSamlRequirements = data
        },

        clearUsernameSamlRequirements(_) {
            state.authUsernameSamlRequirements = {}
        },

        setAuthUsername(state: any, username) {
            state.authUsername = username
        },

        clearAuthUsername(state: any) {
            state.authUsername = null
        },
        
    },
}

export default authModule
